/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: Collaboration
 * (C) Copyright IBM Corp. 2018, 2019
 *
 * US Government Users Restricted Rights - Use, duplication or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 */

define('collaboration/canvaseditor/CanvasEditor',['underscore', '../lib/@waca/core-client/js/core-client/ui/core/Class', '../nls/StringResources', './DefaultAttributes', './tools/ArrowTool', './tools/CropTool', './tools/PenTool', './tools/RectTool', './tools/TextboxTool', './shapes/ArrowShape', './shapes/CropShape', './shapes/PenShape', './shapes/RectShape', './shapes/TextboxShape', 'fabric'], function (_, Class, StringResources, DefaultAttributes, ArrowTool, CropTool, PenTool, RectTool, TextboxTool, ArrowShape, CropShape, PenShape, RectShape, TextboxShape) {
	// eslint-disable-line no-unused-vars
	'use strict';

	var TOOLS = {
		'arrow': new ArrowTool(),
		'crop': new CropTool(),
		'pen': new PenTool(),
		'rect': new RectTool(),
		'textbox': new TextboxTool()
	};

	var SHAPES = {
		'arrow': ArrowShape,
		'crop': CropShape,
		'pen': PenShape,
		'rect': RectShape,
		'textbox': TextboxShape
	};

	var JSON_PROPERTIES = ['top', 'left', 'width', 'height', 'figure', 'pointedDirection', 'cornerStyle', 'cornerColor', 'cornerStrokeColor', 'cornerSize', 'transparentCorners', 'hasBorders', 'strokeWidth', 'rotatingPointOffset', 'fontFamily'];

	var VIRTUAL_CURSOR_X = 100;
	var VIRTUAL_CURSOR_Y = 100;

	var CanvasEditor = Class.extend({

		canvas: null,
		initialState: null,
		actionList: null,
		actionListIndex: null,
		tool: null,
		mouseIsDown: null,
		onCanvasChanged: null,
		onObjectModifiedEventListener: null,
		onObjectRemovedEventListener: null,
		updateChangeListEventListener: null,
		borderColor: null,
		fillColor: null,
		shape: null,
		canvasX: VIRTUAL_CURSOR_X,
		canvasY: VIRTUAL_CURSOR_Y,
		defaultRotationAngle: 10,
		keyPress: false,
		cursor: null,
		zoom: 1,
		clientX: -1,
		clientY: -1,

		/**
   * Called by constructor.
   * @param options {Object} Options
   */
		init: function init(options) {
			CanvasEditor.inherited('init', this, arguments);
			window.fabric.Object.prototype.objectCaching = false;
			this.canvas = new window.fabric.Canvas(options.id, {
				defaultCursor: 'default',
				rotationCursor: 'alias'
			});
			this.onCanvasChanged = options.onCanvasChanged;
			this.canvas.selectionColor = 'rgba(255, 255, 255, 0)';
			this.canvas.selectionBorderColor = 'rgba(31, 87, 164, 1)';
			this.canvas.uniScaleTransform = true;
			this.canvas.isDrawingMode = false;
			this.actionList = [];
			this.actionListIndex = -1;
			this.mouseIsDown = false;
			this.onObjectModifiedEventListener = true;
			this.onObjectRemovedEventListener = true;
			this.updateChangeListEventListener = true;
			this.borderColor = 'black';
			this.fillColor = 'transparent';
			this.canvasContainer = options.canvasContainer;
			this.canvas.on({
				'mouse:down': this.onMouseDown.bind(this),
				'mouse:move': this.onMouseMove.bind(this),
				'mouse:up': this.onMouseUp.bind(this),
				'path:created': this.createPath.bind(this),
				'object:added': this.onObjectAdded.bind(this),
				'object:scaling': this.onObjectScaling.bind(this),
				'object:modified': this.onObjectModified.bind(this),
				'object:removed': this.onObjectRemoved.bind(this),
				'selection:created': this.onSelectionCreated.bind(this),
				'selection:updated': this.onSelectionUpdated.bind(this),
				'selection:cleared': this.onSelectionCleared.bind(this),
				'after:render': this.onAfterRender.bind(this),
				'text:editing:exited': this.onTextEditingExited.bind(this)
			});
		},

		lockObjects: function lockObjects() {
			this.canvas.discardActiveObject();
			this.canvas.requestRenderAll();
			this.canvas.forEachObject(function (object) {
				object.selectable = false;
			});
		},

		setCanvasDimensions: function setCanvasDimensions(newCanvasHeight, newCanvasWidth) {
			this.canvas.setHeight(newCanvasHeight);
			this.canvas.setWidth(newCanvasWidth);
		},

		setImage: function setImage(imgSource, restoreState) {
			var _this = this;

			return new Promise(function (resolve, reject) {
				_this.canvas.setBackgroundImage(imgSource, function () {
					_this.canvas.renderAll();
					_this.initialState = restoreState || _this.serialize();
					_this.reset(true).then(resolve, reject);
				}, {});
			});
		},

		getImage: function getImage() {
			return this.canvas.backgroundImage;
		},

		setColors: function setColors(colors) {
			this.borderColor = colors.borderColor;
			this.fillColor = colors.fillColor;
			if (this.tool) {
				this.tool.setColors(this.canvas, colors);
			}
		},

		setTool: function setTool(tool, colors) {
			if (tool) {
				this.tool = TOOLS[tool];
				this.keyPress = false;
				if (this.canvasX < 0 || this.canvasY < 0 || this.canvasX > this.canvas.width || this.canvasY > this.canvas.height) {
					this.resetVirtualCursorCoordinates();
				}
				this.cursor = this.tool.activate(this.canvas, this.canvasX, this.canvasY);
				this.setColors(colors);
			} else {
				if (this.tool) {
					this.tool.deactivate(this.canvas);
					this.cursor = null;
					this.tool = null;
				}
			}
		},

		setZoom: function setZoom(zoom) {
			this.zoom = zoom;
			this.resetVirtualCursorCoordinates();
		},

		getTool: function getTool() {
			return this.tool;
		},

		resetVirtualCursorCoordinates: function resetVirtualCursorCoordinates() {
			this.canvasX = VIRTUAL_CURSOR_X;
			this.canvasY = VIRTUAL_CURSOR_Y;
			this.clientX = -1;
			this.clientY = -1;
		},

		reset: function reset(excludeChangeList) {
			var _this2 = this;

			var currentState = this.serialize();
			if (!_.isEqual(currentState, this.initialState)) {
				this.canvas.isDrawingMode = false;
				this.canvas.defaultCursor = 'default';
				return this._loadState(this.initialState).then(function () {
					_this2.canvas.requestRenderAll();
					if (!excludeChangeList) {
						_this2.updateChangeList();
					}
				});
			}
			return Promise.resolve(null);
		},

		serialize: function serialize() {
			return this.canvas.toJSON(JSON_PROPERTIES);
		},

		onDelete: function onDelete() {
			if (this.canvas.getActiveObjects().length > 0) {
				this.canvas.isDrawingMode = false;
				this.onObjectRemovedEventListener = false;
				this.canvas.getActiveObjects().forEach(function (object) {
					this.canvas.remove(object);
				}.bind(this));
				if (this.canvas.getActiveObject()) {
					if (this.canvas.getActiveObject().type === 'activeSelection') {
						this.canvas.discardActiveObject();
					}
				}
				this.canvas.renderAll();
				this.onObjectRemovedEventListener = true;
				this.updateChangeList();
			}
		},

		onRotate: function onRotate(direction) {
			if (!this.tool && this.canvas.getActiveObjects().length > 0) {
				if (this.canvas.getActiveObject()) {
					var object = this.canvas.getActiveObject();
					object.rotate((object.angle + direction * this.defaultRotationAngle) % 360);
				}
				this.updateChangeList();
				this.canvas.renderAll();
				return true;
			}
			return false;
		},

		onMouseDown: function onMouseDown(event) {
			// if there are activeObjects, do not mousedown
			// if it is the primary toolbar, do not mousedown
			// if the canvas is in selection mode, do not mousedown
			// if the variable mouseIsDown is true, do not mousedown
			if (typeof this.canvas.getActiveObjects() !== 'undefined' && this.canvas.getActiveObjects().length > 0 || this.tool === null || this.canvas.selection === true || this.mouseIsDown === true) {
				return;
			}
			if (this.tool) {
				this.mouseIsDown = true;
				var pointer = this.canvas.getPointer(event.e);
				this.canvasX = pointer.x;
				this.canvasY = pointer.y;
				this.clientX = event.e.clientX;
				this.clientY = event.e.clientY;
				this.shape = this.tool.create(this.canvas, this.canvasX, this.canvasY);
				this.canvas.renderAll();
			}
		},

		onMouseMove: function onMouseMove(event) {
			var pointer = this.canvas.getPointer(event.e);
			var movement = {
				horizontal: pointer.x - this.canvasX,
				vertical: pointer.y - this.canvasY
			};
			this.canvasX = pointer.x;
			this.canvasY = pointer.y;
			this.clientX = event.e.clientX;
			this.clientY = event.e.clientY;
			if (this.tool) {
				this.canvas.getObjects().forEach(function (object) {
					if (object.figure === 'cursor') {
						object.left = this.canvasX;
						object.top = this.canvasY;
					}
				}.bind(this));
				if (this.mouseIsDown && this.shape) {
					this.shape.resize(this.canvasX, this.canvasY);
				}
				this.canvas.renderAll();
			}
			if (this.mouseIsDown) {
				this.viewportEdgeScroll(movement);
			}
		},

		onMouseUp: function onMouseUp(event) {
			if (this.mouseIsDown && this.tool && this.shape) {
				this.mouseIsDown = false;
				this.keyPress = false;
				this.shape.finish(event, this.canvas);
				this.canvas.renderAll();
				if (this.tool.canUpdate()) {
					this.updateChangeList();
				}
			}
		},

		viewportEdgeScroll: function viewportEdgeScroll(movement) {
			var container = this.canvasContainer;
			var rect = this.canvasContainer.getBoundingClientRect();

			var leftEdge = container.scrollLeft;
			var rightEdge = container.scrollLeft + rect.width;
			var cursorX = this.canvasX * this.zoom;
			if (movement.horizontal < 0 && cursorX <= leftEdge || movement.horizontal > 0 && cursorX >= rightEdge) {
				container.scrollLeft += movement.horizontal;
			}
			var topEdge = container.scrollTop;
			var bottomEdge = container.scrollTop + rect.height;
			var cursorY = this.canvasY * this.zoom;
			if (movement.vertical < 0 && cursorY <= topEdge || movement.vertical > 0 && cursorY >= bottomEdge) {
				container.scrollTop += movement.vertical;
			}
		},

		handleTabKeyEvent: function handleTabKeyEvent(direction) {
			var tabbable = true;
			if (this.canvas.getObjects().length === 0) {
				tabbable = false;
			}
			var canvasObjects = this.canvas.getObjects();
			var activeObjects = this.canvas.getActiveObjects();
			if (canvasObjects.length > 0 && activeObjects.length === 0) {
				if (direction === 'forward') {
					this.canvas.setActiveObject(canvasObjects[0]);
				} else if (direction === 'backward') {
					this.canvas.setActiveObject(canvasObjects[canvasObjects.length - 1]);
				}
			} else if (canvasObjects.length > 0 && activeObjects.length > 0) {
				var index = -1;
				canvasObjects.forEach(function (object, i) {
					if (activeObjects[activeObjects.length - 1] === object) {
						index = i;
						this.canvas.discardActiveObject();
					}
				}.bind(this));
				if (index >= 0 && index < canvasObjects.length) {
					if (direction === 'forward') {
						if (index + 1 !== canvasObjects.length) {
							this.canvas.setActiveObject(canvasObjects[index + 1]);
						} else {
							tabbable = false;
						}
					} else if (direction === 'backward') {
						if (index - 1 !== -1) {
							this.canvas.setActiveObject(canvasObjects[index - 1]);
						} else {
							tabbable = false;
						}
					} else {
						tabbable = false;
					}
				}
			}
			this.canvas.renderAll();
			return tabbable;
		},

		getCursorPositionRelativeToTheScreen: function getCursorPositionRelativeToTheScreen() {
			if (this.clientX === -1 && this.clientY === -1) {
				var offset = this.canvas.calcOffset()._offset;
				return {
					x: this.canvasX * this.zoom + offset.left,
					y: this.canvasY * this.zoom + offset.top
				};
			}

			return {
				x: this.clientX,
				y: this.clientY
			};
		},

		handleEnterKeyEvent: function handleEnterKeyEvent() {
			if (this.tool) {
				var position = this.getCursorPositionRelativeToTheScreen();
				var event1 = {
					clientX: position.x,
					clientY: position.y,
					bubbles: true
				};
				if (!this.keyPress) {
					this.keyPress = !this.keyPress;
					this.canvas.getSelectionElement().dispatchEvent(this.dispatchMouseEvent('mousedown', event1));
				} else {
					this.keyPress = !this.keyPress;
					this.canvas.getSelectionElement().dispatchEvent(this.dispatchMouseEvent('mouseup', event1));
				}
			} else {
				if (this.canvas.getActiveObjects().length > 0) {
					this.keyPress = false;
					this.canvas.getActiveObjects().forEach(function (object) {
						SHAPES[object.figure].handleEnterKeyEvent(object);
					}.bind(this));
				}
			}
		},

		handleArrowKeyEvent: function handleArrowKeyEvent(alignment, amount, moving, event) {
			if (!this.tool && this.canvas.getActiveObjects().length > 0 || this.tool) {
				event.preventDefault();
			}
			var position = this.getCursorPositionRelativeToTheScreen();
			var newClientX = position.x,
			    newClientY = position.y,
			    movement = void 0;
			if (alignment === 'horizontal') {
				newClientX += amount;
				movement = 'left';
			} else {
				newClientY += amount;
				movement = 'top';
			}
			if (!this.tool) {
				var activeObject = this.canvas.getActiveObject();
				if (activeObject) {
					if (moving) {
						var objectCoordinates = activeObject.getCenterPoint();
						this.canvasX = objectCoordinates.x;
						this.canvasY = objectCoordinates.y;
						activeObject[movement] += amount;
						this.viewportEdgeScroll({
							vertical: alignment === 'vertical' ? amount : 0,
							horizontal: alignment === 'horizontal' ? amount : 0
						});
						activeObject.setCoords();
					} else {
						//scaling
						var activeObjects = this.canvas.getActiveObjects();
						activeObjects.forEach(function (object) {
							var height = object.height;
							var width = object.width;
							object.set({
								height: alignment === 'vertical' ? amount + height : height,
								width: alignment === 'horizontal' ? amount + width : width
							});
							object.setCoords();
						});
					}
				}
				this.updateChangeList();
				this.canvas.renderAll();
			} else {
				var mouseMoveEvent = {
					clientX: newClientX,
					clientY: newClientY,
					bubbles: true
				};
				this.canvas.getSelectionElement().dispatchEvent(this.dispatchMouseEvent('mousemove', mouseMoveEvent));
			}
		},

		handleEscapeKeyEvent: function handleEscapeKeyEvent() {
			if (this.mouseIsDown && this.tool && this.shape) {
				this.mouseIsDown = false;
				this.keyPress = false;
				var position = this.getCursorPositionRelativeToTheScreen();
				var event1 = {
					clientX: position.x,
					clientY: position.y,
					bubbles: true
				};
				this.updateChangeListEventListener = false;
				this.canvas.getSelectionElement().dispatchEvent(this.dispatchMouseEvent('mouseup', event1));
				this.updateChangeListEventListener = true;
				var elem = -1;
				var obj = this.canvas.getObjects();
				obj.forEach(function (object, i) {
					if (object === this.shape.shape) {
						elem = i;
					}
				}.bind(this));
				if (elem >= 0) {
					this.onObjectRemovedEventListener = false;
					this.canvas.remove(obj[elem]);
					this.onObjectRemovedEventListener = true;
					this.canvas.renderAll();
				}
			} else if (!this.tool && this.canvas.getActiveObjects().length > 0) {
				this.canvas.discardActiveObject();
				this.canvas.renderAll();
			}
		},

		handleSkewKeyEvent: function handleSkewKeyEvent(amount, event) {
			event.preventDefault();
			// don't skew if there are mulipte objects active
			if (!this.tool && this.canvas.getActiveObjects().length === 1) {
				var activeObject = this.canvas.getActiveObject();
				var Shape = SHAPES[activeObject.figure];
				if (Shape && Shape.canSkew()) {
					activeObject.set({
						height: activeObject.height,
						width: activeObject.width + amount
					});
					activeObject.setCoords();
					this.updateChangeList();
					this.canvas.renderAll();
				}
			}
		},

		dispatchMouseEvent: function dispatchMouseEvent(mouseEvent, event) {
			var evt = window.document.createEvent('MouseEvent');
			evt.initMouseEvent(mouseEvent, true, true, window, 0, 0, 0, event.clientX, event.clientY, false, false, false, false, 0, null);
			return evt;
		},

		dispatchMouseEventUsingKeyboardEvent: function dispatchMouseEventUsingKeyboardEvent(event1, event2) {
			var drawnObject = this.canvas.getSelectionElement();
			drawnObject.dispatchEvent(this.dispatchMouseEvent('mousedown', event1));
			drawnObject.dispatchEvent(this.dispatchMouseEvent('mousemove', event2));
			drawnObject.dispatchEvent(this.dispatchMouseEvent('mouseup', event2));
		},

		createPath: function createPath(event) {
			if (this.tool && this.shape) {
				this.shape.createPath(event.path);
				this.canvas.renderAll();
				this.updateChangeList();
			}
		},

		onObjectAdded: function onObjectAdded() {},

		onObjectModified: function onObjectModified(event) {
			if (this.onObjectModifiedEventListener) {
				var fShape;
				if (event.target.type === 'activeSelection') {
					fShape = event.target;
					if (fShape.scaleX !== 1 || fShape.scaleY !== 1) {
						this.onObjectModifiedEventListener = false;
						this.onGroupScaling(fShape);
						this.onObjectModifiedEventListener = true;
					}
					this.updateChangeList();
				} else {
					fShape = event.target;
					var Shape = SHAPES[fShape.figure];
					if (Shape && Shape.canUpdate()) {
						this.updateChangeList();
					}
				}
			}
		},

		onObjectRemoved: function onObjectRemoved(event) {
			if (this.onObjectRemovedEventListener) {
				if (event.target.figure !== 'cursor') {
					var fShape = event.target,
					    Shape = SHAPES[fShape.figure];
					if (Shape && Shape.canUpdate()) {
						this.updateChangeList();
					}
				}
			}
		},

		onObjectScaling: function onObjectScaling(event) {
			var fShape = event.target,
			    Shape = SHAPES[fShape.figure];
			if (Shape) {
				Shape.scale(fShape);
				this.canvas.renderAll();
			}
		},

		onGroupScaling: function onGroupScaling(fShape) {
			if (fShape.type === 'activeSelection') {
				var Shape;
				var offset = this.canvas.calcOffset()._offset;
				fShape.forEachObject(function (object) {
					this.canvas.discardActiveObject();
					this.canvas.setActiveObject(object);
					Shape = SHAPES[object.figure];
					if (Shape) {
						var event1 = {
							clientX: Math.round(object.aCoords.br.x + offset.left),
							clientY: Math.round(object.aCoords.br.y + offset.top),
							bubbles: true
						};
						var event2 = {
							clientX: Math.round(object.aCoords.br.x + offset.left) + 1,
							clientY: Math.round(object.aCoords.br.y + offset.top),
							bubbles: true
						};
						var event3 = {
							clientX: Math.round(object.aCoords.br.x + offset.left) - 1,
							clientY: Math.round(object.aCoords.br.y + offset.top),
							bubbles: true
						};
						this.updateChangeListEventListener = false;
						this.dispatchMouseEventUsingKeyboardEvent(event1, event2);
						object.setCoords();
						this.dispatchMouseEventUsingKeyboardEvent(event1, event3);
						object.setCoords();
						this.updateChangeListEventListener = true;
					}
				}.bind(this));
				this.canvas.discardActiveObject();
				var group1 = new window.fabric.ActiveSelection(fShape.getObjects(), _.extend({}, DefaultAttributes, {
					canvas: this.canvas,
					strokeWidth: null
				}));
				this.canvas.setActiveObject(group1);
				this.canvas.renderAll();
			}
		},

		onSelectionCreated: function onSelectionCreated(event) {
			if (this.tool) {
				this.tool.onSelectionCreated(event, this.canvas);
			}
			if (event.target.type === 'activeSelection') {
				event.target.set(_.extend({}, DefaultAttributes, {
					strokeWidth: null
				}));
			}
			this.updateToolbarIcons();
		},

		onSelectionUpdated: function onSelectionUpdated(event) {
			if (this.tool) {
				this.tool.onSelectionUpdated(event, this.canvas);
			}
			if (event.target.type === 'activeSelection') {
				event.target.set(_.extend({}, DefaultAttributes, {
					strokeWidth: null
				}));
			}
			this.updateToolbarIcons();
		},

		onSelectionCleared: function onSelectionCleared() {
			this.updateToolbarIcons();
		},

		onAfterRender: function onAfterRender() {
			this.drawControlsVisibility();
		},

		onTextEditingExited: function onTextEditingExited() {
			if (this.canvasContainer) {
				this.canvasContainer.focus();
			}
			if (this.tool) {
				this.canvas.discardActiveObject();
				this.keyPress = false;
				if (this.mouseIsDown) {
					this.mouseIsDown = false;
					this.updateChangeList();
				}
			}
		},

		executeAction: function executeAction(action) {
			if (this.tool && this.tool.executeAction(action, this.canvas)) {
				this.updateChangeList();
				this.canvas.renderAll();
			}
		},

		updateChangeList: function updateChangeList() {
			if (this.updateChangeListEventListener) {
				if (this.cursor) {
					this.canvas.remove(this.cursor);
				}
				if (this.actionListIndex < this.actionList.length - 1) {
					this.actionList.length = this.actionListIndex + 1;
				}
				this.actionList.push(this.serialize());
				this.actionListIndex++;
				if (this.cursor) {
					this.canvas.add(this.cursor);
					this.canvas.renderAll();
				}
				this.updateToolbarIcons();
			}
		},

		updateToolbarIcons: function updateToolbarIcons() {
			if (this.onCanvasChanged) {
				this.onCanvasChanged();
			}
		},

		canDelete: function canDelete() {
			return !this.tool && this.canvas.getActiveObjects().length > 0;
		},

		canUndo: function canUndo() {
			return this.actionListIndex !== -1;
		},

		canRedo: function canRedo() {
			return this.actionList.length !== this.actionListIndex + 1;
		},

		_loadState: function _loadState(state) {
			var _this3 = this;

			return new Promise(function (resolve) {
				_this3.canvas.loadFromJSON(state, function () {
					_this3.canvas.setWidth(state.width);
					_this3.canvas.setHeight(state.height);
					_this3.canvas.setBackgroundImage(state.backgroundImage.src, function () {
						_this3.canvas.renderAll();
						if (_this3.tool) {
							_this3.tool.activate(_this3.canvas, _this3.canvasX, _this3.canvasY);
						}
						resolve();
					}, {
						top: state.backgroundImage.top,
						left: state.backgroundImage.left
					});
				});
			});
		},

		undo: function undo() {
			var _this4 = this;

			return new Promise(function (resolve) {
				if (_this4.actionListIndex > -1) {
					_this4.actionListIndex--;
				}
				if (_this4.actionListIndex === -1) {
					_this4._loadState(_this4.initialState).then(resolve);
				} else {
					_this4._loadState(_this4.actionList[_this4.actionListIndex]).then(resolve);
				}
				_this4.updateToolbarIcons();
			});
		},

		redo: function redo() {
			var _this5 = this;

			return new Promise(function (resolve) {
				if (_this5.actionListIndex < _this5.actionList.length - 1) {
					_this5.actionListIndex++;
				}
				if (_this5.actionListIndex === -1) {
					_this5._loadState(_this5.initialState).then(resolve);
				} else {
					_this5._loadState(_this5.actionList[_this5.actionListIndex]).then(resolve);
				}
				_this5.updateToolbarIcons();
			});
		},

		drawControlsVisibility: function drawControlsVisibility() {
			this.canvas.getObjects().forEach(function (drawnObject) {
				var Shape = SHAPES[drawnObject.figure];
				if (Shape) {
					Shape.drawControlsVisibility(this.canvas, drawnObject);
				}
			}.bind(this));
		},

		export: function _export() {
			var img = this.canvas.toDataURL({
				format: 'png'
			});
			return img;
		}

	});

	return CanvasEditor;
});
;

var _temp = function () {
	if (typeof __REACT_HOT_LOADER__ === 'undefined') {
		return;
	}
}();

;

